import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { useProAuth } from '@/lib/ProAuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Loader2, Calendar as CalendarIcon, Clock, MapPin, Send, 
  Zap, Crown, CheckCircle, AlertCircle, ArrowRight, Sparkles,
  Timer, Rocket, AlertTriangle, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { 
  useSmartDefaults, 
  MicroRewardInline, 
  getRewardMessage,
  ProgressWithLove 
} from '@/components/UserLove';
import { ImageUploadField } from '@/components/ImageUploadField';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map URL slugs to backend category/subcategory IDs
const CATEGORY_SLUG_MAP = {
  // Categories
  'transport': 'transport',
  'auto-repair': 'auto_repair',
  'auto_repair': 'auto_repair',
  'rentals': 'rentals',
  'equipment': 'rentals',
  'subcontracting': 'construction', // Maps to construction for B2B
  'accommodation': 'travel_accommodation',
  
  // Transport subcategories
  'local': 'local_moving',
  'long-distance': 'long_distance',
  'long_distance': 'long_distance',
  'kaugvedu': 'long_distance',
  'moving': 'local_moving',
  'kolimine': 'local_moving',
  'delivery': 'delivery_courier',
  'pakivedu': 'delivery_courier',
  
  // Auto repair subcategories
  'tires': 'tire_change',
  'rehvid': 'tire_change',
  'engine': 'engine',
  'mootor': 'engine',
  'brakes': 'brakes',
  'pidurid': 'brakes',
  'oil-change': 'general_service',
  'oil_change': 'general_service',
  'diagnostics': 'general_service',
  'diagnostika': 'general_service',
  'bodywork': 'bodywork',
  'electrical': 'electrical_auto',
  'transmission': 'transmission',
  'suspension': 'suspension',
  'exhaust': 'exhaust',
  'ac_heating': 'air_conditioning',
  
  // Equipment/Rentals subcategories (maps to general categories)
  'power_tools': 'construction',
  'hand_tools': 'construction',
  'construction': 'construction',
  'garden': 'landscaping',
  'cleaning': 'cleaning',
  'vehicles': 'transport',
  
  // B2B/Subcontracting subcategories
  'electrical_work': 'electrical',
  'plumbing': 'plumbing',
  'hvac': 'hvac',
  'painting': 'painting',
  'welding': 'welding',
  'manufacturing': 'construction',
  'logistics': 'transport',
  'it_services': 'it_services',
  'cleaning_services': 'cleaning'
};

// Frontend category name localization (overrides backend English names)
const CATEGORY_TRANSLATIONS = {
  // Main categories
  'construction': { en: 'Construction & Building', et: 'Ehitus', fi: 'Rakentaminen', nb: 'Bygg og konstruksjon', ru: 'Строительство и ремонт', sv: 'Construction & Building' },
  'electrical': { en: 'Electrical Work', et: 'Elektritööd', fi: 'Sähkötyöt', nb: 'Elektrikerarbeid', ru: 'Электрика', sv: 'Elarbeten' },
  'plumbing': { en: 'Plumbing', et: 'Torutööd', fi: 'Putkityöt', nb: 'Rørlegger', ru: 'Сантехника', sv: 'Rörmokeri' },
  'hvac': { en: 'HVAC / Climate Control', et: 'Küte ja ventilatsioon', fi: 'LVI-työt', nb: 'VVS / Ventilasjon', ru: 'Отопление и вентиляция', sv: 'VVS / Klimat' },
  'cleaning': { en: 'Cleaning Services', et: 'Puhastusteenused', fi: 'Siivouspalvelut', nb: 'Renholdstjenester', ru: 'Клининг', sv: 'Städtjänster' },
  'renovation': { en: 'Renovation', et: 'Renoveerimine', fi: 'Remontointi', nb: 'Oppussing', ru: 'Ремонт', sv: 'Renovering' },
  'painting': { en: 'Painting & Decorating', et: 'Värvimine ja viimistlus', fi: 'Maalaus ja sisustus', nb: 'Maling og dekorering', ru: 'Покраска и отделка', sv: 'Måleri & inredning' },
  'carpentry': { en: 'Carpentry & Woodwork', et: 'Puusepatööd', fi: 'Puusepäntyöt', nb: 'Snekring og trearbeid', ru: 'Столярные работы', sv: 'Snickeri' },
  'landscaping': { en: 'Landscaping & Garden', et: 'Aiatööd', fi: 'Piha ja puutarha', nb: 'Hage og uteområde', ru: 'Благоустройство и сад', sv: 'Trädgård & landskap' },
  'welding': { en: 'Welding & Metalwork', et: 'Keevitus ja metallitööd', fi: 'Hitsaus ja metallityöt', nb: 'Sveising og metallarbeid', ru: 'Сварка и металлоработы', sv: 'Svetsning & metallarbete' },
  'roofing': { en: 'Roofing', et: 'Katusetööd', fi: 'Kattotöyt', nb: 'Takarbeid', ru: 'Кровельные работы', sv: 'Tak' },
  'flooring': { en: 'Flooring', et: 'Põrandatööd', fi: 'Lattiatyöt', nb: 'Gulvarbeid', ru: 'Напольные работы', sv: 'Golv' },
  'it_services': { en: 'IT Services', et: 'IT-teenused', fi: 'IT-palvelut', nb: 'IT-tjenester', ru: 'IT-услуги', sv: 'IT-tjänster' },
  'security': { en: 'Security Services', et: 'Turvateenused', fi: 'Turvapalvelut', nb: 'Sikkerhetstjenester', ru: 'Охранные услуги', sv: 'Säkerhetstjänster' },
  'transport': { en: 'Transport & Moving', et: 'Transport ja kolimine', fi: 'Kuljetus ja muutto', nb: 'Transport og flytting', ru: 'Транспорт и переезд', sv: 'Transport & flytt' },
  'auto_repair': { en: 'Auto Repair', et: 'Autoremont', fi: 'Autokorjaus', nb: 'Bilverksted', ru: 'Автосервис', sv: 'Bilreparation' },
  'travel_accommodation': { en: 'Travel & Accommodation', et: 'Reis ja majutus', fi: 'Matka ja majoitus', nb: 'Reise og overnatting', ru: 'Путешествия и проживание', sv: 'Resa & boende' },
  'other': { en: 'Other', et: 'Muu', fi: 'Muu', nb: 'Annet', ru: 'Другое', sv: 'Annat' },
  
  // Transport subcategories
  'local_moving': { en: 'Local Moving', et: 'Kohalik kolimine', fi: 'Paikallinen muutto', nb: 'Lokal flytting', ru: 'Местный переезд', sv: 'Lokal flytt' },
  'long_distance': { en: 'Long-Distance Moving', et: 'Kaugvedu', fi: 'Pitkän matkan muutto', nb: 'Langdistanseflytting', ru: 'Дальний переезд', sv: 'Långdistansflytt' },
  'delivery_courier': { en: 'Delivery / Courier', et: 'Pakivedu / Kuller', fi: 'Toimitus / Kuriiri', nb: 'Levering / Bud', ru: 'Доставка / Курьер', sv: 'Leverans / Bud' },
  'furniture_transport': { en: 'Furniture Transport', et: 'Mööbli transport', fi: 'Huonekalujen kuljetus', nb: 'Møbeltransport', ru: 'Перевозка мебели', sv: 'Möbeltransport' },
  
  // Auto repair subcategories
  'tire_change': { en: 'Tire Change', et: 'Rehvivahetus', fi: 'Renkaanvaihto', nb: 'Dekkskift', ru: 'Смена шин', sv: 'Däckbyte' },
  'engine': { en: 'Engine Repair', et: 'Mootori remont', fi: 'Moottorin korjaus', nb: 'Motorreparasjon', ru: 'Ремонт двигателя', sv: 'Motorreparation' },
  'bodywork': { en: 'Bodywork / Collision', et: 'Keretööd', fi: 'Korityöt', nb: 'Karosseri / Kollisjon', ru: 'Кузовные работы', sv: 'Karosseri / Kollision' },
  'brakes': { en: 'Brakes', et: 'Pidurid', fi: 'Jarrut', nb: 'Bremser', ru: 'Тормоза', sv: 'Bromsar' },
  'electrical_auto': { en: 'Electrical', et: 'Elektrisüsteem', fi: 'Sähköjärjestelmä', nb: 'Elektrisk', ru: 'Электрика авто', sv: 'El' },
  'suspension': { en: 'Suspension', et: 'Vedrustus', fi: 'Jousitus', nb: 'Understell', ru: 'Подвеска', sv: 'Hjulupphängning' },
  'exhaust': { en: 'Exhaust System', et: 'Heitgaasisüsteem', fi: 'Pakoputkisto', nb: 'Eksosanlegg', ru: 'Выхлопная система', sv: 'Avgassystem' },
  'transmission': { en: 'Transmission', et: 'Käigukast', fi: 'Vaihteisto', nb: 'Girkasse', ru: 'Трансмиссия', sv: 'Växellåda' },
  'air_conditioning': { en: 'Air Conditioning', et: 'Kliimaseade', fi: 'Ilmastointi', nb: 'Klimaanlegg', ru: 'Кондиционер', sv: 'Luftkonditionering' },
  'general_service': { en: 'General Service', et: 'Üldhooldus', fi: 'Yleishuolto', nb: 'Generell service', ru: 'Общее обслуживание', sv: 'Allmän service' },
  
  // Accommodation subcategories
  'short_term_rental': { en: 'Short-Term Rental', et: 'Lühiajaline üür', fi: 'Lyhytaikainen vuokra', nb: 'Korttidsleie', ru: 'Краткосрочная аренда', sv: 'Short-Term Rental' },
  'worker_housing': { en: 'Worker Housing', et: 'Töötaja majutus', fi: 'Työntekijämajoitus', nb: 'Arbeiderbolig', ru: 'Жильё для работников', sv: 'Worker Housing' },
  'travel_assistance': { en: 'Travel Assistance', et: 'Reisiabi', fi: 'Matkaapu', nb: 'Reisehjelp', ru: 'Помощь в путешествии', sv: 'Travel Assistance' },
  'corporate_accommodation': { en: 'Corporate Accommodation', et: 'Ärimajutus', fi: 'Yritysmajoitus', nb: 'Bedriftsinnkvartering', ru: 'Корпоративное проживание', sv: 'Corporate Accommodation' }
};

// Helper function to map URL slug to backend ID
const mapSlugToId = (slug) => {
  if (!slug) return '';
  return CATEGORY_SLUG_MAP[slug] || slug;
};

// Helper function to get localized category/subcategory name
const getLocalizedName = (id, language) => {
  const translations = CATEGORY_TRANSLATIONS[id];
  if (translations) {
    return translations[language] || translations.en;
  }
  return id; // Fallback to ID if no translation
};

// Urgency level configurations
const URGENCY_LEVELS = [
  { 
    id: 'normal', 
    icon: Clock, 
    color: 'text-slate-500',
    bg: 'bg-slate-100',
    label: { en: 'Normal', et: 'Tavaline', fi: 'Normaali' , nb: 'Normal', ru: 'Обычно' },
    desc: { en: 'Within a week', et: 'Nädala jooksul', fi: 'Viikon sisällä' , nb: 'Innen en uke', ru: 'В течение недели' }
  },
  { 
    id: 'soon', 
    icon: Timer, 
    color: 'text-blue-500',
    bg: 'bg-blue-100',
    label: { en: 'Soon', et: 'Peagi', fi: 'Pian' , nb: 'Snart', ru: 'Скоро' },
    desc: { en: '2-3 days', et: '2-3 päeva', fi: '2-3 päivää' , nb: '2-3 days', ru: '2-3 days' }
  },
  { 
    id: 'today', 
    icon: Zap, 
    color: 'text-orange-500',
    bg: 'bg-orange-100',
    label: { en: 'Today', et: 'Täna', fi: 'Tänään' , nb: 'I dag', ru: 'Сегодня' },
    desc: { en: 'Same day', et: 'Samal päeval', fi: 'Samana päivänä' , nb: 'Same day', ru: 'Same day' }
  },
  { 
    id: 'asap', 
    icon: Rocket, 
    color: 'text-red-500',
    bg: 'bg-red-100',
    label: { en: 'ASAP', et: 'Kohe', fi: 'ASAP' , nb: 'ASAP', ru: 'ASAP' },
    desc: { en: 'As soon as possible', et: 'Niipea kui võimalik', fi: 'Mahdollisimman pian' , nb: 'As soon as possible', ru: 'As soon as possible' }
  }
];

/**
 * Universal Booking Form Component
 * With User Love Layer integration
 * Supports deep-linking via URL params and props
 */
const BookingForm = ({ onSuccess, defaultCountry, defaultCity, defaultCategory, defaultSubcategory }) => {
  const { t, language } = useLanguage();
  const { isPro, isAuthenticated } = useProAuth();
  const { rememberBooking } = useSmartDefaults();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showProModal, setShowProModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [rewards, setRewards] = useState({});
  const [images, setImages] = useState([]);  // Image URLs for booking
  
  // Calculate form progress
  const [formProgress, setFormProgress] = useState(0);
  
  // Resolve initial values from URL params or props (URL params take precedence)
  // Map URL slugs to backend category IDs
  const urlCategory = mapSlugToId(searchParams.get('category') || defaultCategory || '');
  const urlSubcategory = mapSlugToId(searchParams.get('subcategory') || defaultSubcategory || '');
  
  const [formData, setFormData] = useState({
    category: urlCategory,
    subcategory: urlSubcategory,
    description: '',
    country: defaultCountry || '',
    city: defaultCity || '',
    preferred_date: '',
    preferred_time: '',
    flexible_time: false,
    urgency: 'normal',
    contact_email: '',
    contact_name: '',
    contact_phone: '',
    quick_response: false,
    language: language || 'en'
  });

  // Apply defaults when they arrive
  useEffect(() => {
    if (defaultCountry && !formData.country) {
      setFormData(prev => ({ ...prev, country: defaultCountry }));
    }
    if (defaultCity && !formData.city) {
      setFormData(prev => ({ ...prev, city: defaultCity }));
    }
  }, [defaultCountry, defaultCity]);

  // Sync category/subcategory from URL params or props when categories are loaded
  useEffect(() => {
    if (categories.length > 0) {
      const rawCat = searchParams.get('category') || defaultCategory;
      const rawSub = searchParams.get('subcategory') || defaultSubcategory;
      
      // Map URL slugs to backend IDs
      const cat = mapSlugToId(rawCat);
      const sub = mapSlugToId(rawSub);
      
      if (cat && !formData.category) {
        setFormData(prev => ({ ...prev, category: cat }));
      }
      if (sub && !formData.subcategory) {
        setFormData(prev => ({ ...prev, subcategory: sub }));
      }
    }
  }, [categories, searchParams, defaultCategory, defaultSubcategory]);

  // Calculate progress when form changes
  useEffect(() => {
    let progress = 0;
    if (formData.category) progress += 2;
    if (formData.preferred_date) progress += 2;
    if (formData.country) progress += 1;
    if (formData.city) progress += 1;
    if (formData.contact_email) progress += 2;
    setFormProgress(progress);
  }, [formData]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/categories`);
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const selectedCategory = categories.find(c => c.id === formData.category);

  // Show micro-reward when field completed
  const showReward = useCallback((field, type = 'form_progress') => {
    setRewards(prev => ({ ...prev, [field]: type }));
    setTimeout(() => {
      setRewards(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }, 2000);
  }, []);

  const handleCategoryChange = (value) => {
    setFormData({ ...formData, category: value, subcategory: '' });
    showReward('category', 'category_selected');
  };

  const handleQuickResponseToggle = (checked) => {
    if (!isPro && checked) {
      setShowProModal(true);
    } else {
      setFormData({ ...formData, quick_response: checked });
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setFormData({ 
      ...formData, 
      preferred_date: date ? format(date, 'yyyy-MM-dd') : '' 
    });
    if (date) showReward('date', 'date_selected');
  };

  const handleUrgencyChange = (urgencyId) => {
    setFormData({ ...formData, urgency: urgencyId });
    showReward('urgency', 'quick_action');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.category) {
      toast.error(t('booking.errors.selectCategory') || 'Please select a service category');
      return;
    }
    if (!formData.preferred_date) {
      toast.error(t('booking.errors.selectDate') || 'Please select a preferred date');
      return;
    }
    if (!formData.country || !formData.city) {
      toast.error(t('booking.errors.selectLocation') || 'Please enter your location');
      return;
    }
    if (!formData.contact_email) {
      toast.error(t('booking.errors.enterEmail') || 'Please enter your email');
      return;
    }

    setLoading(true);
    
    try {
      // Build smart_fields based on category
      const smartFields = {};
      
      if (formData.category === 'auto_repair') {
        if (formData.car_make) smartFields.car_make = formData.car_make;
        if (formData.car_model) smartFields.car_model = formData.car_model;
        if (formData.car_year) smartFields.car_year = formData.car_year;
        if (formData.car_problem) smartFields.car_problem = formData.car_problem;
      }
      
      if (formData.category === 'transport' || formData.category === 'moving') {
        if (formData.pickup_address) smartFields.pickup_address = formData.pickup_address;
        if (formData.dropoff_address) smartFields.dropoff_address = formData.dropoff_address;
        if (formData.transport_items) smartFields.transport_items = formData.transport_items;
        if (formData.floor_info) smartFields.floor_info = formData.floor_info;
      }
      
      if (formData.category === 'accommodation' || formData.category === 'travel' || formData.category === 'travel_accommodation') {
        if (formData.checkin_date) smartFields.checkin_date = formData.checkin_date;
        if (formData.checkout_date) smartFields.checkout_date = formData.checkout_date;
        if (formData.guest_count) smartFields.guest_count = formData.guest_count;
        if (formData.work_related) smartFields.work_related = formData.work_related;
      }

      // Build request payload
      const payload = {
        category: formData.category,
        subcategory: formData.subcategory || null,
        description: formData.description || null,
        country: formData.country,
        city: formData.city,
        preferred_date: formData.preferred_date,
        preferred_time: formData.preferred_time || null,
        flexible_time: formData.flexible_time,
        urgency: formData.urgency,
        contact_email: formData.contact_email,
        contact_name: formData.contact_name || null,
        contact_phone: formData.contact_phone || null,
        quick_response: formData.quick_response,
        smart_fields: Object.keys(smartFields).length > 0 ? smartFields : null,
        images: images.length > 0 ? images : null,  // Include uploaded images
        language: language || 'en'
      };

      const response = await fetch(`${API_URL}/api/bookings/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create booking');
      }

      const data = await response.json();
      
      // Remember this booking for smart defaults
      rememberBooking({
        category: formData.category,
        country: formData.country,
        city: formData.city
      });
      
      // Show success message with personality
      const successMessages = {
        en: "Done! We'll handle the rest.",
        et: "Valmis! Meie hoolitseme ülejäänu eest.",
        fi: "Valmis! Hoidamme loput."
      };
      toast.success(successMessages[language] || successMessages.en);
      
      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate(`/booking/status/${data.booking_id}?token=${data.access_token}`);
      }
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = [
    { value: 'morning', label: t('booking.time.morning') || 'Morning (8-12)' },
    { value: 'afternoon', label: t('booking.time.afternoon') || 'Afternoon (12-17)' },
    { value: 'evening', label: t('booking.time.evening') || 'Evening (17-21)' },
    { value: 'flexible', label: t('booking.time.flexible') || 'Flexible' }
  ];

  const countries = [
    { code: 'EE', name: 'Estonia' },
    { code: 'FI', name: 'Finland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'DE', name: 'Germany' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'PL', name: 'Poland' },
    { code: 'DK', name: 'Denmark' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'FR', name: 'France' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'AT', name: 'Austria' },
    { code: 'CH', name: 'Switzerland' }
  ];

  return (
    <>
      <Card className="shadow-xl border-2 overflow-hidden" data-testid="booking-form">
        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <ProgressWithLove steps={8} currentStep={formProgress} language={language} />
        </div>

        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-orange-500" />
            <CardTitle>{t('booking.title') || 'Book a Service'}</CardTitle>
          </div>
          <CardDescription>
            {t('booking.subtitle') || "Select date and time, we'll match you with available providers"}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Service Category */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('booking.category') || 'Service Category'} *</Label>
                <AnimatePresence>
                  {rewards.category && (
                    <MicroRewardInline 
                      type="category_selected" 
                      language={language}
                      show={true}
                    />
                  )}
                </AnimatePresence>
              </div>
              <Select 
                value={formData.category} 
                onValueChange={handleCategoryChange}
              >
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder={t('booking.selectCategory') || 'Select a category'} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        {getLocalizedName(cat.id, language)}
                        {cat.priority && (
                          <Badge variant="outline" className="text-xs bg-orange-50 text-orange-600">
                            <Zap className="h-3 w-3 mr-1" />
                            {t('booking.fastResponse') || 'Kiire'}
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subcategory (if available) */}
            {selectedCategory?.subcategories && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-2"
              >
                <Label>{t('booking.subcategory') || 'Subcategory'}</Label>
                <Select 
                  value={formData.subcategory} 
                  onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('booking.selectSubcategory') || 'Select subcategory'} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedCategory.subcategories.map(sub => (
                      <SelectItem key={sub.id} value={sub.id}>
                        {getLocalizedName(sub.id, language)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </motion.div>
            )}

            {/* ============================================================ */}
            {/* CATEGORY-SPECIFIC SMART FIELDS */}
            {/* ============================================================ */}
            
            {/* Auto Repair Smart Fields */}
            {formData.category === 'auto_repair' && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4"
              >
                <div className="flex items-center gap-2 text-blue-700 font-medium">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 17h14v-4H5v4zM4 13h16V7H4v6z"/>
                    <circle cx="7.5" cy="17" r="1.5"/>
                    <circle cx="16.5" cy="17" r="1.5"/>
                  </svg>
                  {t('booking.autoRepair.title') || 'Vehicle Information'}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm">{t('booking.autoRepair.make') || 'Make'}</Label>
                    <Input
                      placeholder="e.g. Toyota"
                      value={formData.car_make || ''}
                      onChange={(e) => setFormData({ ...formData, car_make: e.target.value })}
                      data-testid="car-make"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('booking.autoRepair.model') || 'Model'}</Label>
                    <Input
                      placeholder="e.g. Camry"
                      value={formData.car_model || ''}
                      onChange={(e) => setFormData({ ...formData, car_model: e.target.value })}
                      data-testid="car-model"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('booking.autoRepair.year') || 'Year'}</Label>
                    <Input
                      type="number"
                      placeholder="e.g. 2020"
                      min="1990"
                      max="2030"
                      value={formData.car_year || ''}
                      onChange={(e) => setFormData({ ...formData, car_year: e.target.value })}
                      data-testid="car-year"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">{t('booking.autoRepair.problem') || 'Issue Description'}</Label>
                  <Input
                    placeholder={t('booking.autoRepair.problemPlaceholder') || 'e.g. Engine noise, brake issue...'}
                    value={formData.car_problem || ''}
                    onChange={(e) => setFormData({ ...formData, car_problem: e.target.value })}
                  />
                </div>
              </motion.div>
            )}

            {/* Transport Smart Fields */}
            {(formData.category === 'transport' || formData.category === 'moving') && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-4"
              >
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="6" width="22" height="12" rx="2"/>
                    <circle cx="6" cy="18" r="2"/>
                    <circle cx="18" cy="18" r="2"/>
                  </svg>
                  {t('booking.transport.title') || 'Transport Details'}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">{t('booking.transport.pickup') || 'Pickup Address'} *</Label>
                    <Input
                      placeholder={t('booking.transport.pickupPlaceholder') || 'Full pickup address'}
                      value={formData.pickup_address || ''}
                      onChange={(e) => setFormData({ ...formData, pickup_address: e.target.value })}
                      data-testid="pickup-address"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('booking.transport.dropoff') || 'Drop-off Address'} *</Label>
                    <Input
                      placeholder={t('booking.transport.dropoffPlaceholder') || 'Full delivery address'}
                      value={formData.dropoff_address || ''}
                      onChange={(e) => setFormData({ ...formData, dropoff_address: e.target.value })}
                      data-testid="dropoff-address"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">{t('booking.transport.items') || 'What to transport?'}</Label>
                    <Input
                      placeholder={t('booking.transport.itemsPlaceholder') || 'e.g. Furniture, boxes, appliances'}
                      value={formData.transport_items || ''}
                      onChange={(e) => setFormData({ ...formData, transport_items: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('booking.transport.floor') || 'Floor (if applicable)'}</Label>
                    <Input
                      placeholder={t('booking.transport.floorPlaceholder') || 'e.g. 3rd floor, no elevator'}
                      value={formData.floor_info || ''}
                      onChange={(e) => setFormData({ ...formData, floor_info: e.target.value })}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Accommodation Smart Fields */}
            {(formData.category === 'accommodation' || formData.category === 'travel' || formData.category === 'travel_accommodation') && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4"
              >
                <div className="flex items-center gap-2 text-purple-700 font-medium">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10v11M20 10v11M8 14v7M12 14v7M16 14v7"/>
                  </svg>
                  {t('booking.accommodation.title') || 'Accommodation Details'}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm">{t('booking.accommodation.checkin') || 'Check-in Date'}</Label>
                    <Input
                      type="date"
                      value={formData.checkin_date || ''}
                      onChange={(e) => setFormData({ ...formData, checkin_date: e.target.value })}
                      data-testid="checkin-date"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('booking.accommodation.checkout') || 'Check-out Date'}</Label>
                    <Input
                      type="date"
                      value={formData.checkout_date || ''}
                      onChange={(e) => setFormData({ ...formData, checkout_date: e.target.value })}
                      data-testid="checkout-date"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('booking.accommodation.guests') || 'Guests'}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      placeholder="1"
                      value={formData.guest_count || ''}
                      onChange={(e) => setFormData({ ...formData, guest_count: e.target.value })}
                      data-testid="guest-count"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.work_related || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, work_related: checked })}
                    data-testid="work-related"
                  />
                  <Label className="text-sm">{t('booking.accommodation.workRelated') || 'Work-related stay'}</Label>
                </div>
              </motion.div>
            )}

            {/* Urgency Level Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t('booking.urgency') || 'Urgency Level'}</Label>
                <AnimatePresence>
                  {rewards.urgency && (
                    <MicroRewardInline 
                      type="quick_action" 
                      language={language}
                      show={true}
                    />
                  )}
                </AnimatePresence>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {URGENCY_LEVELS.map(level => {
                  const Icon = level.icon;
                  const isSelected = formData.urgency === level.id;
                  return (
                    <button
                      key={level.id}
                      type="button"
                      onClick={() => handleUrgencyChange(level.id)}
                      className={cn(
                        "p-3 rounded-lg border-2 transition-all text-left",
                        isSelected 
                          ? `${level.bg} border-current ${level.color}` 
                          : "bg-white border-slate-200 hover:border-slate-300"
                      )}
                      data-testid={`urgency-${level.id}`}
                    >
                      <Icon className={cn("h-5 w-5 mb-1", isSelected ? level.color : "text-slate-400")} />
                      <div className="text-sm font-medium">{level.label[language] || level.label.en}</div>
                      <div className="text-xs text-slate-500">{level.desc[language] || level.desc.en}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t('booking.preferredDate') || 'Preferred Date'} *</Label>
                  <AnimatePresence>
                    {rewards.date && (
                      <MicroRewardInline 
                        type="date_selected" 
                        language={language}
                        show={true}
                      />
                    )}
                  </AnimatePresence>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !selectedDate && "text-muted-foreground"
                      )}
                      data-testid="date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, 'PPP') : t('booking.pickDate') || 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>{t('booking.preferredTime') || 'Preferred Time'}</Label>
                <Select 
                  value={formData.preferred_time} 
                  onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
                >
                  <SelectTrigger data-testid="time-select">
                    <SelectValue placeholder={t('booking.selectTime') || 'Select time'} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map(slot => (
                      <SelectItem key={slot.value} value={slot.value}>{slot.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Flexible Time Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">{t('booking.flexibleTime') || 'Flexible timing'}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('booking.flexibleTimeDesc') || 'I can adjust to provider availability'}
                </p>
              </div>
              <Switch
                checked={formData.flexible_time}
                onCheckedChange={(checked) => setFormData({ ...formData, flexible_time: checked })}
              />
            </div>

            {/* Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('booking.country') || 'Country'} *</Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger data-testid="country-select">
                    <SelectValue placeholder={t('booking.selectCountry') || 'Select country'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[250px]">
                    {countries.map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('booking.city') || 'City'} *</Label>
                <Input
                  placeholder={t('booking.enterCity') || 'Enter city'}
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  data-testid="city-input"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>{t('booking.description') || 'Description'}</Label>
              <Textarea
                placeholder={t('booking.descriptionPlaceholder') || 'Describe what you need...'}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="description-input"
              />
              <p className="text-xs text-muted-foreground">
                <Sparkles className="h-3 w-3 inline mr-1 text-yellow-500" />
                {t('booking.descriptionHint') || 'More details = better matches'}
              </p>
            </div>

            {/* Image Upload */}
            <ImageUploadField
              images={images}
              onImagesChange={setImages}
              maxImages={8}
              uploadEndpoint="/api/workshop-requests/upload-image"
            />

            {/* Quick Response Toggle (PRO Feature) */}
            <div className={cn(
              "p-4 rounded-xl border-2 transition-all",
              formData.quick_response 
                ? "bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-300" 
                : "bg-slate-50 border-slate-200"
            )}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-lg",
                    formData.quick_response ? "bg-orange-100" : "bg-slate-100"
                  )}>
                    <Zap className={cn(
                      "h-5 w-5",
                      formData.quick_response ? "text-orange-500" : "text-slate-400"
                    )} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-medium">
                        {t('booking.quickResponse.title') || 'Quick Response'}
                      </Label>
                      <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs border-0">
                        <Crown className="h-3 w-3 mr-1" />
                        PRO
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('booking.quickResponse.description') || 'Get responses in 10-15 minutes via SMS'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={formData.quick_response}
                  onCheckedChange={handleQuickResponseToggle}
                  data-testid="quick-response-toggle"
                />
              </div>
              {formData.quick_response && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-3 flex items-center gap-2 text-sm text-orange-700"
                >
                  <Zap className="h-4 w-4" />
                  {t('booking.quickResponse.enabled') || 'Providers will be notified immediately via SMS'}
                </motion.div>
              )}
              {!isPro && !formData.quick_response && (
                <button 
                  type="button"
                  onClick={() => setShowProModal(true)}
                  className="mt-2 text-sm text-orange-600 hover:text-orange-700 underline"
                >
                  {t('booking.quickResponse.upgrade') || 'Upgrade to PRO'}
                </button>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-4 pt-4 border-t">
              <Label className="text-base font-semibold">
                {t('booking.contactInfo') || 'Your Contact Info'}
              </Label>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('booking.email') || 'Email'} *</Label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    required
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('booking.name') || 'Name'}</Label>
                  <Input
                    placeholder={t('booking.namePlaceholder') || 'Your name'}
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('booking.phone') || 'Phone'} ({t('booking.optional') || 'optional'})</Label>
                <Input
                  type="tel"
                  placeholder="+372 5XX XXXX"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full h-12 text-lg bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
              disabled={loading}
              data-testid="submit-booking"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  {t('common.sending') || 'Sending...'}
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  {t('booking.submit') || 'Send Booking Request'}
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              <Shield className="h-3 w-3 inline mr-1" />
              {t('booking.privacyNote') || 'Your contact info is shared only with matched providers'}
            </p>
          </form>
        </CardContent>
      </Card>

      {/* PRO Upgrade Modal */}
      <ProUpgradeModal 
        open={showProModal} 
        onClose={() => setShowProModal(false)} 
        t={t}
        language={language}
      />
    </>
  );
};

/**
 * PRO Upgrade Modal
 */
const ProUpgradeModal = ({ open, onClose, t, language }) => {
  const navigate = useNavigate();
  
  const benefits = [
    t('booking.proModal.benefit1') || "Instant SMS notifications to providers",
    t('booking.proModal.benefit2') || "Priority placement in queues",
    t('booking.proModal.benefit3') || "Responses in 10-15 minutes",
    t('booking.proModal.benefit4') || "Included in PRO subscription"
  ];

  const handleUpgrade = () => {
    onClose();
    navigate('/pro/login');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="pro-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-yellow-500" />
            {t('booking.proModal.title') || 'Upgrade to PRO'}
          </DialogTitle>
          <DialogDescription>
            {t('booking.proModal.description') || 'Get faster responses with Quick Response'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
            <ul className="space-y-2">
              {benefits.map((benefit, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="text-center">
            <span className="text-2xl font-bold text-orange-600">€9.99</span>
            <span className="text-muted-foreground">/month</span>
          </div>
          <div className="flex flex-col gap-2">
            <Button 
              className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
              onClick={handleUpgrade}
            >
              <Crown className="h-4 w-4 mr-2" />
              {t('booking.proModal.cta') || 'Upgrade Now'}
            </Button>
            <Button variant="ghost" onClick={onClose} className="w-full">
              {t('booking.proModal.later') || 'Maybe Later'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingForm;
